/**
 * The opposing force (02 §9). Three layers, one file each:
 *
 *  1. perception.ts - a detection meter per soldier (02 §6.2).
 *  2. this file     - the state machine: guard, patrol, suspicious, investigate, search...
 *  3. tactics.ts, fight.ts, errands.ts - squad behaviour inside a firefight (02 §9.4):
 *     temperament decides who suppresses, who flanks, who hides and peeks, who runs for
 *     the alarm, who drags a wounded comrade clear, and who cracks and bolts.
 */

import { bark, sendToInvestigate } from '../alert';
import type { Ctx } from '../ctx';
import { atan2 } from '../math';
import { stepExecutioner } from '../objectives/executioners';
import { ENEMY, secs, TACTICS } from '../tuning';
import type { Enemy, SpawnDef, World } from '../types';
import { createEnemy } from './create';
import { assignRescuers, stepFlee, stepRescue, stepRunner } from './errands';
import { enterCombat, searchSeconds, stepCombat } from './fight';
import { requestPath, travel, turnToward } from './movement';
import { perceive } from './perception';
import { separateEnemies } from './separation';
import { shootAtRanger } from './shooting';
import { stepSpecial, stepSpecialCombat, sweepDeparted } from './specials';

export { createEnemy };

export function stepEnemy(ctx: Ctx, e: Enemy): void {
  const w = ctx.world;
  e.prevX = e.x;
  e.prevZ = e.z;
  e.sinceShot++;
  if (e.fireCooldown > 0) e.fireCooldown--;
  if (e.suppressTicks > 0) e.suppressTicks--;
  if (e.barkTicks > 0) e.barkTicks--;
  if (e.state === 'dead') return;
  // HAZ: mini-bunkers, minitanks and turret-bunker gunners never run the infantry states (./specials).
  if (stepSpecial(ctx, e)) return;

  if (e.state === 'wounded') {
    e.moving = false;
    // He calls out until someone drags him clear - which is also how his friends find you.
    if (!e.stabilised && !e.draggedByRanger) {
      e.stateTicks--;
      if (e.stateTicks <= 0) {
        e.stateTicks = TACTICS.woundedCryTicks;
        bark(ctx, e, 'help');
      }
    }
    return;
  }

  if (e.state === 'down') {
    e.moving = false;
    e.stateTicks--;
    if (e.stateTicks <= 0) {
      e.state = 'search';
      e.stateTicks = secs(searchSeconds(ctx));
      e.detection = 1;
    }
    return;
  }

  if ((w.tick + e.thinkSlot) % ENEMY.perceptionEveryTicks === 0) perceive(ctx, e);
  if (e.detection >= 1 && e.seesRanger) enterCombat(ctx, e);

  // OBJ: mission 10's executioners have a job that comes before everything but a ranger in plain sight.
  if (w.objective.executioners.length > 0 && stepExecutioner(ctx, e)) return;

  // A man in a nest or bunker never leaves it: he watches his arc and fires.
  if (e.mans !== null) {
    e.moving = false;
    if (e.state === 'combat') {
      if (e.seesRanger) {
        turnToward(e, atan2(w.ranger.x - e.x, w.ranger.z - e.z), 4);
        const helpless = w.ranger.status === 'downed' && w.ranger.sinceShot > TACTICS.holdFireAfterTicks;
        if (e.reactTicks > 0) e.reactTicks--;
        else if (!helpless) shootAtRanger(ctx, e);
      } else if (e.lostTicks > secs(4)) {
        e.state = 'guard';
        e.detection = 0.3;
      }
    } else {
      turnToward(e, e.homeFacing, 2);
    }
    return;
  }

  switch (e.state) {
    case 'guard': {
      e.moving = false;
      // Bored sentries glance left, ahead, right of their post, a few seconds each.
      const glance = (((Math.floor(w.tick / secs(3.2)) + e.id) % 3) - 1) * 0.6;
      turnToward(e, e.homeFacing + glance, 1.2);
      if (e.detection >= ENEMY.suspiciousAt) {
        e.state = 'suspicious';
        e.stateTicks = ENEMY.suspiciousTicks;
        e.targetX = w.ranger.x;
        e.targetZ = w.ranger.z;
      }
      break;
    }
    case 'patrol': {
      const node = e.patrol[e.patrolIndex];
      if (node) {
        if (e.stateTicks > 0) {
          e.stateTicks--;
          e.moving = false;
        } else if (travel(ctx, e, node[0], node[1], ENEMY.walkSpeed)) {
          e.patrolIndex = (e.patrolIndex + 1) % e.patrol.length;
          e.stateTicks = secs(1 + ctx.rng.float() * 2);
          e.path = [];
        }
      }
      if (e.detection >= ENEMY.suspiciousAt) {
        e.state = 'suspicious';
        e.stateTicks = ENEMY.suspiciousTicks;
      }
      break;
    }
    case 'suspicious': {
      e.moving = false;
      turnToward(e, atan2(e.targetX - e.x, e.targetZ - e.z), 5);
      e.stateTicks--;
      if (e.stateTicks <= 0) {
        // The cautious wait to be sure; everyone else walks over for a look.
        if (e.detection >= (e.persona === 'cautious' ? 0.6 : ENEMY.suspiciousAt)) sendToInvestigate(e, e.targetX, e.targetZ, false);
        else e.state = 'return';
      }
      break;
    }
    case 'investigate': {
      const hurry = w.alert >= 2 ? ENEMY.huntSpeed : ENEMY.walkSpeed * 1.2;
      if (travel(ctx, e, e.targetX, e.targetZ, hurry)) {
        e.state = 'search';
        e.stateTicks = secs(searchSeconds(ctx) * (w.alert >= 2 ? 1 : 0.35));
        e.path = [];
        bark(ctx, e, 'searching');
      }
      break;
    }
    case 'combat':
      // HAZ: the flamethrower and the rocket trooper fight with their own weapon (./specials).
      if (!stepSpecialCombat(ctx, e)) stepCombat(ctx, e);
      break;
    case 'runner':
      stepRunner(ctx, e);
      break;
    case 'flee':
      stepFlee(ctx, e);
      break;
    case 'rescue':
      stepRescue(ctx, e);
      break;
    case 'search': {
      e.stateTicks--;
      // Look around; every few seconds poke at a spot nearby.
      if (e.path.length === 0 || travel(ctx, e, e.targetX, e.targetZ, ENEMY.walkSpeed)) {
        e.moving = false;
        turnToward(e, e.facing + 1.5, 1.6);
        if (e.stateTicks % secs(3.5) === 0) {
          e.targetX = Math.min(ctx.statics.def.sizeX - 2, Math.max(2, e.x + ctx.rng.range(-7, 7)));
          e.targetZ = Math.min(ctx.statics.def.sizeZ - 2, Math.max(2, e.z + ctx.rng.range(-7, 7)));
          e.path = [];
          requestPath(ctx.statics, e, e.targetX, e.targetZ);
        }
      }
      if (e.stateTicks <= 0) {
        e.state = 'return';
        e.wary = true;
        e.path = [];
        bark(ctx, e, 'allClear');
      }
      break;
    }
    case 'return': {
      const node = e.duty === 'patrol' ? e.patrol[e.patrolIndex] : undefined;
      if (travel(ctx, e, node ? node[0] : e.homeX, node ? node[1] : e.homeZ, ENEMY.walkSpeed)) {
        e.state = e.duty;
        e.stateTicks = 0;
        e.path = [];
      }
      if (e.detection >= ENEMY.suspiciousAt) {
        e.state = 'suspicious';
        e.stateTicks = ENEMY.suspiciousTicks;
      }
      break;
    }
  }
}

export function stepEnemies(ctx: Ctx): void {
  if (ctx.world.tick % 30 === 0) assignRescuers(ctx);
  for (const e of ctx.world.enemies) stepEnemy(ctx, e);
  // Nobody finishes the tick standing inside somebody else (separation.ts).
  separateEnemies(ctx);
  // R4: a vehicle that has driven off the end of its run leaves the map (specials/vehicleCrew.ts).
  sweepDeparted(ctx);
  // A runner who was stopped frees the role for someone else.
  const runner = ctx.world.enemies.find((e) => e.id === ctx.world.runnerId);
  if (ctx.world.runnerId !== -1 && (!runner || runner.state !== 'runner')) ctx.world.runnerId = -1;
}

/** Reinforcements walk on from the north edge and head for the last disturbance (02 §9.3). */
export function spawnReinforcements(ctx: Ctx, count: number): void {
  const w: World = ctx.world;
  const def = ctx.statics.def;
  const baseX = Math.min(def.sizeX - 8, Math.max(6, w.disturbanceX + ctx.rng.range(-12, 12)));
  for (let i = 0; i < count; i++) {
    const spawn: SpawnDef = { kind: 'rifleman', x: baseX + i * 1.6, z: 2.5, facing: 0 };
    const e = createEnemy(w.nextId++, spawn, w.enemies.length, ctx.rng.float(), ctx.rng.float(), def.threat);
    e.duty = 'patrol';
    e.patrol = [[w.disturbanceX, w.disturbanceZ], [def.pickup.x, def.pickup.z]];
    e.wary = true;
    w.enemies.push(e);
    sendToInvestigate(e, w.disturbanceX, w.disturbanceZ, true);
  }
  ctx.events.push({ type: 'reinforcements', count });
}
